import { CellObject } from 'xlsx';
import {
    HealthPlanCopayRecord,
    HealthPlanLifeRecord,
    HealthPlanParseResult,
} from '../healthPlanTypes';
import {
    loadXlsx,
    readRows,
    readFileAsTextWithFallback,
    findSheet,
    normalizeCpf,
    normalizeUserCode,
    toNumber,
    serialToDate,
    parseBradescoDate
} from './parserUtils';


function consolidateLives(records: HealthPlanLifeRecord[]): HealthPlanLifeRecord[] {
    const map = new Map<string, HealthPlanLifeRecord>();

    records.forEach((record) => {
        const beneficiaryCpf = normalizeCpf(record.beneficiaryCpf);
        const holderCpf = normalizeCpf(record.holderCpf);
        const nameKey = (record.beneficiaryName || '').toLowerCase().trim();
        const key = beneficiaryCpf || (holderCpf && nameKey ? `${holderCpf}|${nameKey}` : nameKey);

        const existing = map.get(key);
        if (!existing) {
            map.set(key, record);
            return;
        }

        const mergedPlanNames = Array.from(
            new Set(
                [existing.planName, record.planName].filter(Boolean),
            ),
        ).join(' / ');

        const premiumTotal = (existing.premiumTotal ?? 0) + (record.premiumTotal ?? 0);
        const copayAmount = (existing.copayAmount ?? 0) + (record.copayAmount ?? 0);
        map.set(key, {
            ...existing,
            premiumTotal,
            copayAmount,
            totalCost: premiumTotal + copayAmount,
            planName: mergedPlanNames || existing.planName || record.planName,
            inclusionDate: existing.inclusionDate ?? record.inclusionDate ?? null,
            relationship: existing.relationship || record.relationship,
            holderCpf: existing.holderCpf || record.holderCpf,
            beneficiaryCpf: existing.beneficiaryCpf || record.beneficiaryCpf,
            userCode: existing.userCode || record.userCode,
            plans: [...(existing.plans ?? []), ...(record.plans ?? [])],
        });
    });

    return Array.from(map.values());
}


type NotredameCredentialInfo = {
    certificate: string;
    familyIdentifier: string;
    userCode: string;
    suffix: string;
};

function parseNotredameCredential(value: string): NotredameCredentialInfo {
    const raw = (value || '').trim();
    if (!raw) return { certificate: '', familyIdentifier: '', userCode: '', suffix: '' };
    const [basePart, suffixPartRaw = ''] = raw.split('-');
    const base = (basePart || '').trim();
    const suffix = suffixPartRaw.trim().split(/\s+/)[0] ?? '';
    const certificate = suffix ? `${base}-${suffix}` : base;
    const userCode = certificate.replace(/[^0-9A-Za-z]/g, '');
    const familyIdentifier = base.replace(/[^0-9A-Za-z]/g, '') || base;
    return { certificate, familyIdentifier, userCode, suffix };
}

function parseNotredameLivesFromXlsx(
    rows: (CellObject | string | number | null)[][],
    xlsx: typeof import('xlsx'),
): HealthPlanLifeRecord[] {
    const headerIndex = rows.findIndex((cols) => (cols[0]?.toString() || '').toLowerCase().startsWith('empresa'));
    if (headerIndex === -1) return [];
    const dataRows = rows.slice(headerIndex + 1);
    const lives: HealthPlanLifeRecord[] = [];
    const familyGroups = new Map<string, HealthPlanLifeRecord[]>();
    const seen = new Set<string>();

    dataRows.forEach((cols) => {
        if (!cols || cols.length < 5) return;
        const firstCol = (cols[0]?.toString() ?? '').toLowerCase();
        const beneficiaryName = (cols[6]?.toString() ?? '').trim();
        const isHeaderRow =
            firstCol.startsWith('empresa') ||
            firstCol.includes('analitico') ||
            beneficiaryName.toLowerCase().includes('beneficiario') ||
            (cols[12]?.toString() ?? '').toLowerCase().includes('plano');
        if (!beneficiaryName || isHeaderRow) return;

        const credentialRaw = cols[3]?.toString() ?? '';
        const credentialInfo = parseNotredameCredential(credentialRaw);
        const matriculaRaw = (cols[4]?.toString() ?? '').trim();
        const cpfValue = normalizeCpf(cols[5]?.toString());
        if (cpfValue.length !== 11) return;
        const relationshipRaw = (cols[11]?.toString() ?? '').trim();
        const planName = (cols[12]?.toString() ?? '').trim();
        const uniqueKey = `${credentialInfo.certificate || matriculaRaw}|${cpfValue}|${beneficiaryName.toLowerCase()}|${planName}`;
        if (seen.has(uniqueKey)) return;
        seen.add(uniqueKey);

        let premiumValue: number | null = null;
        const val18 = cols[18];
        const val14 = cols[14];

        if (typeof val18 === 'number') premiumValue = val18;
        else if (typeof val14 === 'number') premiumValue = val14;
        else {
            const v18 = toNumber(val18?.toString());
            const v14 = toNumber(val14?.toString());
            premiumValue = v18 ?? v14;
        }

        if (premiumValue != null) {
            premiumValue = premiumValue / 100;
        }

        const inclusionDate = serialToDate(cols[9], xlsx);
        const isHolder = relationshipRaw.toUpperCase().includes('TIT');
        const life: HealthPlanLifeRecord = {
            beneficiaryName,
            beneficiaryCpf: cpfValue,
            holderCpf: isHolder ? cpfValue : '',
            userCode: credentialInfo.userCode || normalizeUserCode(matriculaRaw),
            birthDate: serialToDate(cols[8], xlsx),
            relationship: relationshipRaw || (isHolder ? 'Titular' : 'Dependente'),
            planName: planName || 'Plano',
            inclusionDate,
            premiumTotal: premiumValue,
            copayAmount: null,
            totalCost: premiumValue ?? 0,
            plans: [
                {
                    planName: planName || 'Plano',
                    premiumTotal: premiumValue,
                    copayAmount: null,
                },
            ],
            copayEntries: [],
            certificateCode: credentialInfo.certificate || matriculaRaw || null,
            familyIdentifier: credentialInfo.familyIdentifier || normalizeUserCode(matriculaRaw) || null,
            isPrimaryHolder: isHolder,
        };

        lives.push(life);
        const familyKey =
            life.familyIdentifier || life.userCode || life.beneficiaryCpf || normalizeUserCode(matriculaRaw) || beneficiaryName;
        if (!familyGroups.has(familyKey)) {
            familyGroups.set(familyKey, []);
        }
        familyGroups.get(familyKey)?.push(life);
    });

    familyGroups.forEach((familyLives) => {
        const holder = familyLives.find((life) => life.isPrimaryHolder) ?? familyLives[0];
        if (!holder) return;
        familyLives.forEach((life) => {
            if (life === holder) return;
            if (!life.holderCpf) life.holderCpf = holder.beneficiaryCpf || holder.holderCpf || '';
        });
    });

    return consolidateLives(
        lives.map((life) => ({
            ...life,
            totalCost: (life.premiumTotal ?? 0) + (life.copayAmount ?? 0),
        })),
    );
}

function parseNotredameCopayFromXlsx(
    rows: (CellObject | string | number | null)[][],
    xlsx: typeof import('xlsx'),
): HealthPlanCopayRecord[] {
    const headerIndex = rows.findIndex((cols) => cols.some((value) => value?.toString().toLowerCase().includes('fatura')));
    if (headerIndex === -1) return [];
    const headerRow = rows[headerIndex].map((value) => value?.toString().toLowerCase() ?? '');
    const findIndex = (label: string) => headerRow.findIndex((value) => value.includes(label));
    const idxCpf = findIndex('cpf');
    const idxBeneficiary = findIndex('beneficiario');
    const idxValue = findIndex('vl participacao');
    const idxContract = findIndex('contrato');
    const idxMatricula = findIndex('matricula');
    const idxVencimento = findIndex('vencimento');

    return rows
        .slice(headerIndex + 1)
        .map((cols) => {
            const valueToken = idxValue >= 0 ? cols[idxValue] : null;
            let value: number | null = null;

            if (typeof valueToken === 'number') value = valueToken;
            else value = toNumber(valueToken?.toString());

            if (value != null) {
                value = value / 100;
            }

            if (value == null) return null;
            const beneficiaryRaw = idxBeneficiary >= 0 ? cols[idxBeneficiary]?.toString() ?? '' : '';
            const [beneficiaryCode = '', beneficiaryNameRaw = ''] = beneficiaryRaw.split('-');
            const beneficiaryName = beneficiaryNameRaw.trim() || (idxContract >= 0 ? cols[idxContract]?.toString().trim() ?? '' : '') || 'Beneficiário não identificado';
            const cpf = idxCpf >= 0 ? cols[idxCpf]?.toString() ?? '' : '';
            const matricula = idxMatricula >= 0 ? cols[idxMatricula]?.toString() ?? '' : '';
            const vencimento = idxVencimento >= 0 ? cols[idxVencimento] : null;

            return {
                type: 'COPAY',
                beneficiaryName,
                beneficiaryCpf: normalizeCpf(cpf),
                holderCpf: null,
                holderName: idxContract >= 0 ? cols[idxContract]?.toString().trim() ?? null : null,
                holderUserCode: normalizeUserCode(matricula),
                userCode: normalizeUserCode(beneficiaryCode),
                productCode: null,
                description: 'Coparticipação',
                procedure: beneficiaryName,
                participation: null,
                quantity: null,
                percentage: null,
                additionalQuantity: null,
                filmArea: null,
                filmValue: null,
                serviceDate: serialToDate(vencimento, xlsx),
                serviceTime: null,
                value,
                flag: null,
                raw: cols.map(c => c?.toString() ?? '').join('\t'),
            } as HealthPlanCopayRecord;
        })
        .filter(Boolean) as HealthPlanCopayRecord[];
}


function parseDelimitedText(content: string, delimiter: string): string[][] {
    return content
        .split(/\r?\n/)
        .map((rawLine) => rawLine.replace(/^\uFEFF/, '').trim())
        .filter((line) => line.length > 0)
        .map((line) => {
            const split = line.split(delimiter); // Simple split, maybe import splitDelimitedLine if strict
            return split.map(c => c.replace(/^"+|"+$/g, '').trim())
        });
}

function parseNotredameLivesFromCsv(text: string): HealthPlanLifeRecord[] {
    const rows = parseDelimitedText(text, ';');
    const headerIndex = rows.findIndex((cols) => (cols[0] || '').toLowerCase().startsWith('empresa'));
    if (headerIndex === -1) return [];
    const dataRows = rows.slice(headerIndex + 1);
    const lives: HealthPlanLifeRecord[] = [];
    const familyGroups = new Map<string, HealthPlanLifeRecord[]>();
    const seen = new Set<string>();

    dataRows.forEach((cols) => {
        if (!cols || cols.length < 5) return;
        const firstCol = (cols[0] ?? '').toLowerCase();
        const beneficiaryName = (cols[6] ?? '').trim();
        const isHeaderRow =
            firstCol.startsWith('empresa') ||
            firstCol.includes('analitico') ||
            beneficiaryName.toLowerCase().includes('beneficiario') ||
            (cols[12] ?? '').toLowerCase().includes('plano');
        if (!beneficiaryName || isHeaderRow) return;

        const credentialRaw = cols[3] ?? '';
        const credentialInfo = parseNotredameCredential(credentialRaw);
        const matriculaRaw = (cols[4] ?? '').toString().trim();
        const cpfValue = normalizeCpf(cols[5]);
        if (cpfValue.length !== 11) return;
        const relationshipRaw = (cols[11] ?? '').trim();
        const planName = (cols[12] ?? '').trim();
        const uniqueKey = `${credentialInfo.certificate || matriculaRaw}|${cpfValue}|${beneficiaryName.toLowerCase()}|${planName}`;
        if (seen.has(uniqueKey)) return;
        seen.add(uniqueKey);

        const rawPremiumValue = toNumber(cols[18]) ?? toNumber(cols[14]) ?? null;
        const premiumValue = rawPremiumValue != null ? rawPremiumValue / 100 : null;
        const inclusionDate = parseBradescoDate(cols[9]) ?? null;
        const isHolder = relationshipRaw.toUpperCase().includes('TIT');
        const life: HealthPlanLifeRecord = {
            beneficiaryName,
            beneficiaryCpf: cpfValue,
            holderCpf: isHolder ? cpfValue : '',
            userCode: credentialInfo.userCode || normalizeUserCode(matriculaRaw),
            birthDate: parseBradescoDate(cols[8]) ?? null,
            relationship: relationshipRaw || (isHolder ? 'Titular' : 'Dependente'),
            planName: planName || 'Plano',
            inclusionDate,
            premiumTotal: premiumValue,
            copayAmount: null,
            totalCost: premiumValue ?? 0,
            plans: [
                {
                    planName: planName || 'Plano',
                    premiumTotal: premiumValue,
                    copayAmount: null,
                },
            ],
            copayEntries: [],
            certificateCode: credentialInfo.certificate || matriculaRaw || null,
            familyIdentifier: credentialInfo.familyIdentifier || normalizeUserCode(matriculaRaw) || null,
            isPrimaryHolder: isHolder,
        };

        lives.push(life);
        const familyKey =
            life.familyIdentifier || life.userCode || life.beneficiaryCpf || normalizeUserCode(matriculaRaw) || beneficiaryName;
        if (!familyGroups.has(familyKey)) {
            familyGroups.set(familyKey, []);
        }
        familyGroups.get(familyKey)?.push(life);
    });

    familyGroups.forEach((familyLives) => {
        const holder = familyLives.find((life) => life.isPrimaryHolder) ?? familyLives[0];
        if (!holder) return;
        familyLives.forEach((life) => {
            if (life === holder) return;
            if (!life.holderCpf) life.holderCpf = holder.beneficiaryCpf || holder.holderCpf || '';
        });
    });

    return consolidateLives(
        lives.map((life) => ({
            ...life,
            totalCost: (life.premiumTotal ?? 0) + (life.copayAmount ?? 0),
        })),
    );
}

function parseNotredameCopayCsv(text: string): HealthPlanCopayRecord[] {
    const rows = parseDelimitedText(text, '\t');
    const headerIndex = rows.findIndex((cols) => cols.some((value) => value.toLowerCase().includes('fatura')));
    if (headerIndex === -1) return [];
    const headerRow = rows[headerIndex].map((value) => value.toLowerCase());
    const findIndex = (label: string) => headerRow.findIndex((value) => value.includes(label));
    const idxCpf = findIndex('cpf');
    const idxBeneficiary = findIndex('beneficiario');
    const idxValue = findIndex('vl participacao');
    const idxContract = findIndex('contrato');
    const idxMatricula = findIndex('matricula');
    const idxVencimento = findIndex('vencimento');

    return rows
        .slice(headerIndex + 1)
        .map((cols) => {
            const valueToken = idxValue >= 0 ? cols[idxValue] : null;
            const value = toNumber(valueToken);
            if (value == null) return null;
            const beneficiaryRaw = idxBeneficiary >= 0 ? cols[idxBeneficiary] ?? '' : '';
            const [beneficiaryCode = '', beneficiaryNameRaw = ''] = beneficiaryRaw.split('-');
            const beneficiaryName = beneficiaryNameRaw.trim() || (idxContract >= 0 ? cols[idxContract]?.trim() ?? '' : '') || 'Beneficiário não identificado';
            const cpf = idxCpf >= 0 ? cols[idxCpf] ?? '' : '';
            const matricula = idxMatricula >= 0 ? cols[idxMatricula] ?? '' : '';
            const vencimento = idxVencimento >= 0 ? cols[idxVencimento] ?? '' : '';
            return {
                type: 'COPAY',
                beneficiaryName,
                beneficiaryCpf: normalizeCpf(cpf),
                holderCpf: null,
                holderName: idxContract >= 0 ? cols[idxContract]?.trim() ?? null : null,
                holderUserCode: normalizeUserCode(matricula),
                userCode: normalizeUserCode(beneficiaryCode),
                productCode: null,
                description: 'Coparticipação',
                procedure: beneficiaryName,
                participation: null,
                quantity: null,
                percentage: null,
                additionalQuantity: null,
                filmArea: null,
                filmValue: null,
                serviceDate: parseBradescoDate(vencimento) ?? null,
                serviceTime: null,
                value,
                flag: null,
                raw: cols.join('\t'),
            } as HealthPlanCopayRecord;
        })
        .filter(Boolean) as HealthPlanCopayRecord[];
}

function attachCopayEntriesToLives(
    lives: HealthPlanLifeRecord[],
    copayEntries: HealthPlanCopayRecord[],
): HealthPlanLifeRecord[] {
    const livesWithCopay = lives.map((life) => ({
        ...life,
        totalCost: life.totalCost ?? (life.premiumTotal ?? 0) + (life.copayAmount ?? 0),
        copayEntries: [...(life.copayEntries ?? [])],
    }));

    const lifeByUserCode = new Map<string, HealthPlanLifeRecord>();
    livesWithCopay.forEach((life) => {
        const code = normalizeUserCode(life.userCode);
        if (!code) return;
        if (!lifeByUserCode.has(code)) {
            lifeByUserCode.set(code, life);
        }
    });

    copayEntries.forEach((entry) => {
        const code = normalizeUserCode(entry.userCode);
        if (!code) return;
        const life = lifeByUserCode.get(code);
        if (!life) return;
        if (!life.copayEntries) life.copayEntries = [];
        life.copayEntries.push(entry);
    });

    return livesWithCopay;
}


export async function parseNotredameHealthPlanFiles(files: File[]): Promise<HealthPlanParseResult> {
    const monthlyFile =
        files.find((file) => file.name.toLowerCase().includes('mensalidade')) ??
        files.find((file) => file.name.toLowerCase().includes('mensal')) ??
        files.find((file) => file.name.toLowerCase().endsWith('.csv')) ??
        files.find((file) => file.name.toLowerCase().endsWith('.xls')) ??
        files.find((file) => file.name.toLowerCase().endsWith('.xlsx')) ??
        null;

    if (!monthlyFile) {
        throw new Error('Arquivo de mensalidade da Notredame (.csv, .xls, .xlsx) é obrigatório.');
    }

    let lives: HealthPlanLifeRecord[] = [];
    const isExcel = monthlyFile.name.toLowerCase().endsWith('.xls') || monthlyFile.name.toLowerCase().endsWith('.xlsx');

    if (isExcel) {
        const xlsx = await loadXlsx();
        const wb = await xlsx.read(await monthlyFile.arrayBuffer(), { type: 'array' });
        const sheetName = wb.SheetNames[0];
        const sheet = wb.Sheets[sheetName];
        const rows = readRows(sheet, xlsx);
        lives = parseNotredameLivesFromXlsx(rows, xlsx);
    } else {
        const monthlyText = await readFileAsTextWithFallback(monthlyFile);
        lives = parseNotredameLivesFromCsv(monthlyText);
    }

    let copayEntries: HealthPlanCopayRecord[] = [];
    const copayFile =
        files.find((file) => file.name.toLowerCase().includes('copart')) ??
        files.find((file) => file.name.toLowerCase().includes('copay')) ??
        null;

    if (copayFile) {
        try {
            const isCopayExcel = copayFile.name.toLowerCase().endsWith('.xls') || copayFile.name.toLowerCase().endsWith('.xlsx');
            if (isCopayExcel) {
                const xlsx = await loadXlsx();
                const wb = await xlsx.read(await copayFile.arrayBuffer(), { type: 'array' });
                const sheetName = wb.SheetNames[0];
                const sheet = wb.Sheets[sheetName];
                const rows = readRows(sheet, xlsx);
                copayEntries = parseNotredameCopayFromXlsx(rows, xlsx);
            } else {
                const copayText = await readFileAsTextWithFallback(copayFile);
                copayEntries = parseNotredameCopayCsv(copayText);
            }
        } catch (err) {
            console.error('[healthPlanParser] falha ao ler copay da Notredame', err);
        }
    }

    const livesWithCopay = attachCopayEntriesToLives(lives, copayEntries);
    const premiumHealth = livesWithCopay.reduce((acc, life) => acc + (life.premiumTotal ?? 0), 0);
    const activeLives = livesWithCopay.length;
    const copayTotal = copayEntries.reduce((acc, entry) => acc + (entry.value ?? 0), 0);
    const totalCost = premiumHealth + copayTotal;

    return {
        lives: livesWithCopay,
        movements: [],
        copayEntries,
        totals: {
            premiumHealth: Number.isFinite(premiumHealth) ? premiumHealth : null,
            copayTotal: Number.isFinite(copayTotal) ? copayTotal : null,
            activeLives: Number.isFinite(activeLives) ? activeLives : null,
            totalCost: Number.isFinite(totalCost) ? totalCost : null,
        },
        debugRawPages: undefined,
    };
}
